import React from 'react'
import PropTypes from 'prop-types'
import { ScrollErrorForm, FileInput, RadioGroup } from 'components'
import { Formik, Field } from 'formik'
import * as Yup from 'yup'
import { SubmitButton } from 'lp-components'
import { map } from 'lodash'
import { ReasonRadioGroupOptions, RELATED_OPTIONS } from 'config/portal'

const propTypes = {
  initialValues: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
}

const defaultProps = {
  recordkeeperMaterials: [],
}

const reasonValues = map(ReasonRadioGroupOptions, 'value')

function ManualFileUploadForm({ handleSubmit, initialValues }) {
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={Yup.object().shape({
        uploadedFile: Yup.mixed().required('Upload a document to continue'),
        description: Yup.string()
          .trim()
          .required('This field is required')
          .max(200, 'Must be 200 characters or less'),
        relatedServices: Yup.array()
          .of(Yup.string())
          .min(1, 'This field is required'),
        reason: Yup.string().oneOf(reasonValues),
      })}
    >
      {({
        setFieldValue,
        setFieldError,
        setFieldTouched,
        isSubmitting,
        touched,
        errors,
        values,
      }) => {
        return (
          <ScrollErrorForm>
            <>
              <div className="form-container">
                <h6>Upload your file*</h6>
                <FileInput
                  name="uploadedFile"
                  accept={{
                    'text/csv': ['.csv'],
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                      ['.xlsx'],
                    'application/vnd.ms-excel': ['.xls'],
                  }}
                  subtext={<p>Supports: CSV, XLSX. Max file size: 30MB</p>}
                  handleError={(message) => {
                    setFieldValue('uploadedFile', '', false)
                    setFieldTouched('uploadedFile', true, false)
                    setFieldError('uploadedFile', message)
                  }}
                  invalidTypeErrorMessage="Only csv and xls are supported. Please provide another document."
                  sizeTooLargeErrorMessage="File must be 30MB or less."
                  maxSize={30 * 1024 * 1024}
                />
              </div>
              <div className="file-description">
                <h6>File description *</h6>
                <Field
                  component="textarea"
                  id="description"
                  name="description"
                  onChange={(e) => {
                    setFieldValue('description', e.target.value)
                    setFieldTouched('description', true, false)
                  }}
                  placeholder="Enter your file description"
                />
                {touched.description && errors.description && (
                  <div className="error-message">{errors.description}</div>
                )}
              </div>
              <div className="related-services">
                <h6>Related services *</h6>
                {RELATED_OPTIONS.map((opt) => {
                  const checked = values.relatedServices.includes(opt.value)
                  return (
                    <label key={opt.value} className="related-services-options">
                      <input
                        type="checkbox"
                        name="relatedServices"
                        checked={checked}
                        onChange={() => {
                          const next = new Set(values.relatedServices)
                          checked ? next.delete(opt.value) : next.add(opt.value)
                          setFieldValue('relatedServices', Array.from(next))
                        }}
                      />
                      <span>{opt.label}</span>
                    </label>
                  )
                })}
              </div>
              {touched.relatedServices && errors.relatedServices && (
                <div className="error-message">{errors.relatedServices}</div>
              )}
              <div className="reason">
                <h6>
                  Reason <span className="optional-text">(optional)</span>
                </h6>
                <RadioGroup
                  options={ReasonRadioGroupOptions}
                  name="reason"
                  label={false}
                  className="radio-container-input"
                />
              </div>
            </>
            <div className="form-actions">
              <SubmitButton submitting={isSubmitting}>Submit</SubmitButton>
            </div>
          </ScrollErrorForm>
        )
      }}
    </Formik>
  )
}

ManualFileUploadForm.propTypes = propTypes
ManualFileUploadForm.defaultProps = defaultProps

export default ManualFileUploadForm
